import React, { useState } from 'react';
import { shape, string, bool, number } from 'prop-types';
import { Icon } from '@fiverr-private/fit';
import { Image } from '@fiverr-private/media';
import {
    s_fast_clock,
    l_arrow_right,
    EyeIcon,
    HeartDetailedIcon,
    BagDetailedIcon,
    CompassDetailedIcon,
} from '@fiverr-private/icons';
import { BriefIcon, HistoryIcon } from '@fiverr-private/visuals';
import { translate } from '@fiverr-private/i18n-react';
import { LazyFlag } from '../LazyFlag';
import SearchThinIcon from '../../../../../../assets/search_thin_icon.svg';
import BadgeIcon from '../../../../../../assets/badge_icon.svg';
import FreelancerIcon from '../../../../../../assets/freelancer_icon.svg';
import UGCIcon from '../../../../../../assets/ugc_icon.svg';
import WebsiteDevelopmentIcon from '../../../../../../assets/website_development_icon.svg';
import VideoEditingIcon from '../../../../../../assets/video_editing_icon.svg';
import SocialMediaMarketingIcon from '../../../../../../assets/social_media_marketing_icon.svg';

import {
    ARROW_ICONS_SIZE,
    DYNAMIC_MATCHING_SOURCE,
    EXTRACT_PARAM_IDENTIFIER,
    ICONS_SIZE,
    PARAMS,
    PARAMS_MATCHER,
    SYSTEM_ICON_SIZE_LG,
} from './constants';
import { getIconUrlBySubCategoryId } from './utils';
import { ParameterizedIcon } from './parameterizedIcon';
import './style.scss';

export const ParameterizedText = ({
    section,
    className,
    withIcon = true,
    serviceId = null,
    componentDataSource = null,
}) => {
    const [shouldShowFallbackImageData, setShouldShowFallbackImageData] = useState({});

    if (!section || !section.value) {
        return null;
    }

    // Split by params existence, but keep them as is in the result array
    const paramsSections = section.value.split(PARAMS_MATCHER).filter((curr) => !!curr);

    return (
        <span className={className}>
            {paramsSections.map((curr, idx) => {
                const [, param, text] = curr.match(EXTRACT_PARAM_IDENTIFIER) || [];

                if (!param) {
                    /* this is for when last_search's carousel source is dynamic_matching, we want to show the translated text,
                    (LATEST_BRIEF identifier) and not the text received from go_p13n containing the last search term,
                    we assume that last_search carousel will always be sent with non-null componentDataSource */
                    if (componentDataSource === DYNAMIC_MATCHING_SOURCE) {
                        return null;
                    }

                    return <span key={idx}>{curr}</span>;
                } // if its not a param - its considered as regular text
                if (!withIcon) {
                    return text;
                } else {
                    switch (param) {
                        case PARAMS.COUNTRY_FLAG.identifier:
                            return <LazyFlag key={idx} countryCode={section[PARAMS.COUNTRY_FLAG.dataKey]} />;
                        case PARAMS.ICON_URGENT.identifier:
                            return (
                                <ParameterizedIcon key={idx} size={ICONS_SIZE}>
                                    <Icon size={ICONS_SIZE} className="urgent-icon">
                                        {s_fast_clock}
                                    </Icon>
                                </ParameterizedIcon>
                            );
                        case PARAMS.ICON_ARROW.right:
                            return (
                                <ParameterizedIcon key={idx} size={ICONS_SIZE}>
                                    <Icon size={ARROW_ICONS_SIZE} className="arrow-right-icon">
                                        {l_arrow_right}
                                    </Icon>
                                </ParameterizedIcon>
                            );
                        case PARAMS.EYE.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.EYE.identifier} size={ICONS_SIZE}>
                                    <EyeIcon size={ICONS_SIZE} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.HEART.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.HEART.identifier} size={ICONS_SIZE}>
                                    <HeartDetailedIcon size={ICONS_SIZE} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.BAG.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.BAG.identifier} size={ICONS_SIZE}>
                                    <BagDetailedIcon size={ICONS_SIZE} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.COMPASS.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.COMPASS.identifier} size={ICONS_SIZE}>
                                    <CompassDetailedIcon size={ICONS_SIZE} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.BADGE_ICON.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.BADGE_ICON.identifier} size={ICONS_SIZE}>
                                    <img src={BadgeIcon} width={`${ICONS_SIZE}px`} height={`${ICONS_SIZE}px`} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.FREELANCER_ICON.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.FREELANCER_ICON.identifier} size={ICONS_SIZE}>
                                    <img src={FreelancerIcon} width={`${ICONS_SIZE}px`} height={`${ICONS_SIZE}px`} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.SEARCH.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.SEARCH.identifier} size={ICONS_SIZE}>
                                    <img src={SearchThinIcon} width={`${ICONS_SIZE}px`} height={`${ICONS_SIZE}px`} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.BRIEF_OR_SEARCH.identifier:
                            const iconComponent =
                                componentDataSource === DYNAMIC_MATCHING_SOURCE ? (
                                    <BriefIcon size={SYSTEM_ICON_SIZE_LG} />
                                ) : (
                                    <img src={SearchThinIcon} width={`${ICONS_SIZE}px`} height={`${ICONS_SIZE}px`} />
                                );

                            return (
                                <ParameterizedIcon key={PARAMS.BRIEF_OR_SEARCH.identifier} size={ICONS_SIZE}>
                                    {iconComponent}
                                </ParameterizedIcon>
                            );
                        case PARAMS.BRIEF.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.BRIEF.identifier} size={ICONS_SIZE}>
                                    <BriefIcon size={SYSTEM_ICON_SIZE_LG} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.LATEST_BRIEF.identifier:
                            /* when last_search's source is dynamic_matching, we want to show the translated text
                             i.e : <brief_icon> From your latest brief
                             when from a different source we want to show the last search term (from go_p13n)
                             i.e : <search_icon> "website design"
                             */
                            return componentDataSource === DYNAMIC_MATCHING_SOURCE ? (
                                <span key={idx}>{translate(`personalized_content.dynamic_matching.tab_title`)}</span>
                            ) : null;
                        case PARAMS.UGC_ICON.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.UGC_ICON.identifier} size={ICONS_SIZE}>
                                    <img src={UGCIcon} width={`${ICONS_SIZE}px`} height={`${ICONS_SIZE}px`} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.WEBSITE_DEVELOPMENT_ICON.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.WEBSITE_DEVELOPMENT_ICON.identifier} size={ICONS_SIZE}>
                                    <img
                                        src={WebsiteDevelopmentIcon}
                                        width={`${ICONS_SIZE}px`}
                                        height={`${ICONS_SIZE}px`}
                                    />
                                </ParameterizedIcon>
                            );
                        case PARAMS.VIDEO_EDITING_ICON.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.VIDEO_EDITING_ICON.identifier} size={ICONS_SIZE}>
                                    <img src={VideoEditingIcon} width={`${ICONS_SIZE}px`} height={`${ICONS_SIZE}px`} />
                                </ParameterizedIcon>
                            );
                        case PARAMS.SOCIAL_MEDIA_MARKETING_ICON.identifier:
                            return (
                                <ParameterizedIcon
                                    key={PARAMS.SOCIAL_MEDIA_MARKETING_ICON.identifier}
                                    size={ICONS_SIZE}
                                >
                                    <img
                                        src={SocialMediaMarketingIcon}
                                        width={`${ICONS_SIZE}px`}
                                        height={`${ICONS_SIZE}px`}
                                    />
                                </ParameterizedIcon>
                            );
                        case PARAMS.SERVICE_ICON.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.SERVICE_ICON.identifier} size={ICONS_SIZE}>
                                    <Image
                                        src={getIconUrlBySubCategoryId(
                                            shouldShowFallbackImageData[idx] ? 'fallback' : serviceId
                                        )}
                                        height={ICONS_SIZE}
                                        width={ICONS_SIZE}
                                        onError={() =>
                                            setShouldShowFallbackImageData({
                                                ...shouldShowFallbackImageData,
                                                [idx]: true,
                                            })
                                        }
                                        alt="Service"
                                    />
                                </ParameterizedIcon>
                            );
                        case PARAMS.HISTORY_ICON.identifier:
                            return (
                                <ParameterizedIcon key={PARAMS.HISTORY_ICON.identifier} size={ICONS_SIZE}>
                                    <HistoryIcon size={ICONS_SIZE} />
                                </ParameterizedIcon>
                            );
                        default:
                            return null;
                    }
                }
            })}
        </span>
    );
};

ParameterizedText.propTypes = {
    section: shape({
        value: string,
        type: string,
    }),
    className: string,
    withIcon: bool,
    serviceId: number,
    componentDataSource: string,
};

export default ParameterizedText;
